"use client";

import { SLGradientBackground } from "@/components/other/SLGradientBackground";
import { assetPaths } from "@/lib/assetPaths";
import { Box, SxProps, Theme } from "@mui/material";
import { CSSProperties, useEffect, useRef, useState } from "react";

const VIDEO_SRC = assetPaths.videos.headerVideo;

export function SLLazyBackgroundVideo({
  sx,
  videoStyle,
}: {
  sx?: SxProps<Theme>;
  videoStyle?: CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const loadVideo = () => {
      const video = videoRef.current;
      if (!video || video.src) {
        return;
      }

      video.src = VIDEO_SRC;
      video.load();
      void video.play().catch(() => {});
    };

    if (typeof IntersectionObserver === "undefined") {
      loadVideo();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadVideo();
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        ...sx,
      }}
    >
      <SLGradientBackground />
      <video
        ref={videoRef}
        preload="none"
        autoPlay
        loop
        muted
        playsInline
        onCanPlay={() => setIsVideoReady(true)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: isVideoReady ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          ...videoStyle,
        }}
      />
    </Box>
  );
}
